/**
 * ADR-0185 Addendum 3 — the shell/view seam, made structural.
 *
 * The board SHELL is domain-blind: it owns the route, the composed read, the
 * situation line, the solve / what-if / promotion gestures, the what-changed
 * strip and the inputs-changed light — everything that reads only the
 * model/template/proposal contract. Everything that knows what KIND of work a
 * template schedules — what to draw — is a VIEW, registered here by template.
 *
 * A future archetype ADDS a view module and registers it from views/index.ts;
 * the shell never changes. A template with no registered view gets the shell's
 * honest empty state instead of a wrong drawing.
 */
import type { ComponentType } from "react";
import type { BoardPayload, BoardProposalView, Placement, ScenarioEntry } from "./boardTypes.js";

/**
 * The contract a view renders against. The shell owns every write: overlay
 * asserts/clears and scenario-entry edits arrive as callbacks, so a view draws
 * gestures but never talks to the API itself.
 */
export type BoardViewProps = {
  /** The whole composed read — model, template cards, overlays, authority. */
  payload: BoardPayload;
  /** The proposal to draw: the scenario when one is on screen, else the standing plan; null before the first solve. */
  view: BoardProposalView | null;
  /** True while the what-if frame is active. */
  scenario: boolean;
  /** Hypothetical disruption entries collected in the active scenario. */
  scenarioEntries: ScenarioEntry[];
  /** True while a solve or mutation is in flight — views disable their write gestures. */
  busy: boolean;
  /**
   * Addendum 5 — an opaque focus key handed back from the Next-up face's
   * onOpenBoard (the view minted it, the shell never reads it): the view
   * opens with that lane expanded. Null when no jump happened.
   */
  initialFocus: string | null;
  onAssertOverlay: (entry: ScenarioEntry & { note: string | null }) => void;
  onClearOverlay: (overlayId: string) => void;
  onRemoveScenarioEntry: (index: number) => void;
  /**
   * Phase B — the Refine loop's writes, shell-owned like every other write:
   * the pending moves replaced whole (the shell PUTs and reloads; a refusal
   * comes back as the hub's named message), the replan, the two resets, and
   * promoting the refined plan. `refineSolve` is the in-flight replan (for
   * the dock's honest progress).
   */
  refine: {
    setMoves: (moves: Placement[]) => Promise<string | null>;
    replan: () => void;
    reset: (to: "plex" | "optimized") => void;
    promote: () => void;
    inFlight: { stage: string | null; elapsedS: number } | null;
    /** The what-changed of the last replan this session landed, for the attribution strip. */
    lastLanded: BoardProposalView | null;
    dismissLanded: () => void;
  };
};

/**
 * Addendum 5 — the contract the Next-up TAB's face renders against: a
 * read-only face of the standing plan. Clicking a machine row calls
 * onOpenBoard with a focus key the board view understands; the shell relays
 * it blind.
 */
export type NextUpViewProps = {
  payload: BoardPayload;
  /** The standing plan; null before the first solve. */
  view: BoardProposalView | null;
  onOpenBoard: (focus: string) => void;
};

const registry = new Map<string, ComponentType<BoardViewProps>>();
const nextUpRegistry = new Map<string, ComponentType<NextUpViewProps>>();

/** Register a view for a shipped template. Double registration is a wiring bug — refuse loudly. */
export function registerBoardView(templateId: string, view: ComponentType<BoardViewProps>): void {
  if (registry.has(templateId)) {
    throw new Error(`A board view is already registered for template ${templateId}.`);
  }
  registry.set(templateId, view);
}

/** The registered view for a template, or null — the shell then renders its empty state. */
export function boardViewFor(templateId: string): ComponentType<BoardViewProps> | null {
  return registry.get(templateId) ?? null;
}

/** Register a template's Next-up tab face (Addendum 5). Double registration refuses loudly. */
export function registerNextUpView(templateId: string, view: ComponentType<NextUpViewProps>): void {
  if (nextUpRegistry.has(templateId)) {
    throw new Error(`A next-up view is already registered for template ${templateId}.`);
  }
  nextUpRegistry.set(templateId, view);
}

/** The template's Next-up face, or null — the tab then renders the honest empty state. */
export function nextUpViewFor(templateId: string): ComponentType<NextUpViewProps> | null {
  return nextUpRegistry.get(templateId) ?? null;
}
